import React from 'react';
import {
  Alert,
  AlertTitle,
  Box, Button, Card, CardContent, Container, Skeleton, Stack, Tooltip, Typography,
} from '@mui/material';
import {
  BackButton, OutlinedIcons, ZSpinner,
} from '@marageti/z4-lib';
import { SalesRecording, SalesRecordingAggregateStatus, SalesRecordingStatus } from '@marageti/z4-sdk/lib/travel/SalesRecording';
import { Doc } from '@marageti/z4-sdk/lib/documents';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import PartnerInvoiceTable from './partner-invoice-table';

type ViewProps = {
  disableExportWhileProcessing: boolean;
  aggregateStatus: SalesRecordingAggregateStatus | undefined;
  docError: ApiError | undefined;
  salesRecordingsError: ApiError | undefined;
  isLoadingExport: boolean;
  loading: boolean;
  onClickDocument: (document: Doc) => void;
  onClickExport: () => void;
  salesRecordings: SalesRecording[] | [];
  selectedMonth: Date | null;
  totalCommission: string | number | undefined;
  totalSales: string | number | undefined;
  updateStatus: (salesRecordingId: string, salesRecordingStatus: SalesRecordingStatus) => void;
};

const getExportDisabled = (aggregateStatus: SalesRecordingAggregateStatus | undefined, totalCommission: string | number | undefined) => {
  const isNegativeCommission = totalCommission !== undefined
    && Number(String(totalCommission).replace(/[^0-9.-]/g, '')) < 0;
  if (isNegativeCommission) {
    return true;
  }
  switch (aggregateStatus) {
    case SalesRecordingAggregateStatus.SalesRecordingAggregateStatusApproved:
    case SalesRecordingAggregateStatus.SalesRecordingAggregateStatusError:
    case SalesRecordingAggregateStatus.SalesRecordingAggregateStatusProcessing:
      return false;
    default:
      return true;
  }
};

const getTooltipTitle = (aggregateStatus: SalesRecordingAggregateStatus | undefined, totalCommission: string | number | undefined) => {
  const isNegativeCommission = totalCommission !== undefined
    && Number(String(totalCommission).replace(/[^0-9.-]/g, '')) < 0;
  if (isNegativeCommission) {
    return 'Cannot export when total commission is negative';
  }
  switch (aggregateStatus) {
    case SalesRecordingAggregateStatus.SalesRecordingAggregateStatusUnapproved:
      return 'You may export once fully approved';
    case SalesRecordingAggregateStatus.SalesRecordingAggregateStatusInvoiced:
      return 'These have already been invoiced to QuickBooks.';
    case SalesRecordingAggregateStatus.SalesRecordingAggregateStatusProcessing:
      return 'Export in progress...';
    default:
      return '';
  }
};

const PartnerInvoiceView = ({
  disableExportWhileProcessing,
  aggregateStatus,
  docError,
  salesRecordingsError,
  isLoadingExport,
  loading,
  onClickDocument,
  onClickExport,
  salesRecordings,
  selectedMonth,
  totalCommission,
  totalSales,
  updateStatus,
}: ViewProps) => {
  const {
    OutlinedCheckCircleIcon,
  } = OutlinedIcons;

  const approvedAndInvoicedCount = salesRecordings.filter((recording) => recording.status === SalesRecordingStatus.VerifiedStatus || recording.status === SalesRecordingStatus.InvoicedStatus).length;
  const totalCount = salesRecordings.length;
  const exportDisabled = getExportDisabled(aggregateStatus, totalCommission) || loading || isLoadingExport || disableExportWhileProcessing;
  // Determine state flags for conditional rendering
  const isFullyApprovedOrInvoiced = approvedAndInvoicedCount === totalCount;

  const countStyle = {
    color: isFullyApprovedOrInvoiced ? '#008147' : '#4A5152',
    fontWeight: isFullyApprovedOrInvoiced ? 'bold' : 'normal',
  };

  if (salesRecordingsError) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error" sx={{ mt: 4 }}>
          <AlertTitle>There was an issue fetching the sales recordings</AlertTitle>
          {salesRecordingsError.message}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box
        sx={{
          pt: { desktop: 4.5, mobile: 6.5 },
          pb: 20,
          maxWidth: { desktop: 'xl' },
          margin: { desktop: '0 auto 56px' },
        }}
      >
        <BackButton
          aria-label="back to invoices"
          defaultPath="/sales/invoices"
          sx={{ ml: -1, mb: 2 }}
        />
        {docError && (
          <Alert severity="error">
            <AlertTitle>There was an issue downloading the document</AlertTitle>
            {docError.message}
            {docError.code && `Code: ${docError.code} `}
            {docError.id && `ID: ${docError.id}`}
          </Alert>
        )}
        <Stack direction={{ desktop: 'row' }} justifyContent="space-between" alignItems={{ desktop: 'center' }}>
          <Box>
            <Typography variant="h2" sx={{ mb: 0.5 }}>
              {salesRecordings[0]?.partnerName ? salesRecordings[0]?.partnerName : <Skeleton variant="text" width={300} />}
            </Typography>
            <Typography color="primary.light">
              {salesRecordings.length > 0
                ? `Invoice Review - ${selectedMonth ? selectedMonth.toLocaleString('default', { month: 'long', year: 'numeric' }) : 'All'}`
                : <Skeleton variant="text" width={200} />}
            </Typography>
          </Box>
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: { mobile: 1, desktop: 0 },
          }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', color: countStyle.color }} gap={0.25}>
              <OutlinedCheckCircleIcon sx={countStyle} />
              <div style={countStyle}>
                {`${approvedAndInvoicedCount}/${totalCount}`}
              </div>
            </Box>
            <Tooltip
              disableHoverListener={!exportDisabled}
              title={getTooltipTitle(aggregateStatus, totalCommission)}
              placement="top"
            >
              <div>
                <Button
                  variant="contained"
                  disabled={exportDisabled}
                  onClick={onClickExport}
                  sx={{
                    marginLeft: { mobile: '0px', desktop: '16px' },
                  }}
                  type="button"
                >
                  Export
                </Button>
              </div>
            </Tooltip>
          </Box>
        </Stack>
        <Box sx={{ marginTop: { desktop: 4, mobile: 2 } }}>
          {loading ? (
            <Box display="flex" justifyContent="center">
              <ZSpinner />
            </Box>
          ) : null}

          {!loading && salesRecordings.length > 0 ? (
            <PartnerInvoiceTable
              salesRecordings={salesRecordings}
              onClickDocument={onClickDocument}
              updateStatus={updateStatus}
              totalSales={totalSales}
              totalCommission={totalCommission}
            />
          ) : null}

          {!loading && salesRecordings.length === 0 ? (
            <Card>
              <CardContent>
                <Typography variant="body" color="primary.light" textAlign="center">
                  There are no sales recordings for the selected month and status.
                </Typography>
              </CardContent>
            </Card>
          ) : null}
        </Box>
      </Box>
    </Container>
  );
};

export default PartnerInvoiceView;
